import { useI18n } from "vue-i18n";
import axios from "axios";

import apiClient, { optionalParams } from "@/services/apiClient";

/**
 * @typedef {Object} Ticket
 * @property {string} ticketNumber - Numero identificativo del ticket
 * @property {string} subject - Oggetto del ticket
 * @property {string} statusType - Stato del ticket (es. "Open", "On Hold")
 * @property {string} createdTime - Data creazione
 * @property {string} [cf_short_title] - Titolo breve personalizzato
 * @property {string} [cf_servizi] - Tipologia servizio associato
 */

export const TicketService = {
  /**
   * Recupera la lista dei ticket filtrata per servizio e stato.
   * @param {Object} [params]
   * @param {number} [params.page_size=10] - Numero di risultati per pagina
   * @param {number} [params.page=1] - Pagina corrente
   * @param {string} [params.service] - Tipo di servizio (es. "Incident,ChangeRequest")
   * @param {string} [params.status] - Stato filtro (es. "Open,On Hold")
   * @param {string} [params.search_by] - Testo di ricerca libera
   * @param {string} [params.createdTimeRange] - Filtro periodo di creazione
   * @returns {Promise<{data: Ticket[]|null, headers?: string[], count?: number, error: string|null, status?: number|null}>}
   *   headers = colonne tabella (vuote se non ci sono risultati), count = totale risultati;
   *   status = codice HTTP in caso di errore (404 = nessun risultato)
   */
  async getTicketList({
    page_size = 10,
    page = 1,
    service,
    status,
    search_by,
    createdTimeRange,
  } = {}) {
    try {
      const body = optionalParams({ page_size, page, service, status, search_by, createdTimeRange });
      const { data: response } = await apiClient.post(
        "tickets/v1/ticket-list",
        body
      );
      const list = response?.data ?? response ?? [];
      return {
        data: Array.isArray(list) ? list : [],
        headers: response?.headers ?? [], // colonne tabella: assenti se non ci sono risultati
        count: response?.count ?? 0,
        error: null,
      };
    } catch (e) {
      return { data: null, error: e.userMessage ?? e.message, status: e.response?.status ?? null };
    }
  },
};

export function buildSolutionLabel(solutionData, assetType) {
  console.log(assetType);
  const datacenter = solutionData.building ? `${solutionData.building} - ` : "";
  if (assetType === 'cross-connects' && solutionData.tipo) {
    return solutionData.tipo;
  }
  return `${datacenter}${solutionData.solution}`;
}

export function getBackUrlFromAssetType(assetType) {
  const i18n = useI18n();
  if (assetType === "cross-connects") {
    return "CrossConnectList" + i18n.locale.value;
  }
  return (
    assetType.charAt(0).toUpperCase() +
    assetType.slice(1, assetType.length) +
    i18n.locale.value
  );
}

export function checkDuplicateTicketForAsset(
  assetType,
  assetId,
  ticketCategory,
  headers = {}
) {
  const normalizeDuplicatePayload = (payload = {}) => {
    const data = { ...payload };

    const candidateLists = [
      data.duplicateTicketIds,
      data.duplicate_ticket_ids,
      data.duplicatedTicketIds,
      data.duplicated_ticket_ids,
      data.duplicates,
    ];

    let duplicateTicketIds =
      candidateLists.find((value) => Array.isArray(value) && value.length) || [];

    if (!duplicateTicketIds.length) {
      const singleId =
        data.duplicated ??
        data.duplicate ??
        data.ticket_id ??
        data.ticketId ??
        null;
      if (singleId) {
        duplicateTicketIds = [singleId];
      }
    }

    const isDuplicatedValue =
      typeof data.isDuplicated === "boolean"
        ? data.isDuplicated
        : typeof data.is_duplicated === "boolean"
        ? data.is_duplicated
        : duplicateTicketIds.length > 0;

    return {
      ...data,
      duplicateTicketIds,
      isDuplicated: isDuplicatedValue,
    };
  };

  if (!assetType || !assetId || !ticketCategory) {
    return Promise.resolve({
      data: { isDuplicated: false, duplicateTicketIds: [] },
    });
  }

  const normalizedCategory = String(ticketCategory)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/__+/g, "_")
    .toLowerCase();

  if (!normalizedCategory) {
    return Promise.resolve({
      data: { isDuplicated: false, duplicateTicketIds: [] },
    });
  }

  const params = new URLSearchParams();
  params.set("asset_id", assetId);
  params.set("asset", assetId);
  params.set("asset_type", assetType);
  params.set("ticket_category", normalizedCategory);
  params.set("ticket_type", normalizedCategory);

  const GET_DUPLICATES_URL = `tickets/v2/ticket/duplicate?${params.toString()}`;

  return axios
    .get(window.VUE_APP_MISTRA_URL + GET_DUPLICATES_URL, {
      headers,
    })
    .then((response) => ({
      ...response,
      data: normalizeDuplicatePayload(response?.data),
    }));
}

export function getIncidentSeverityLevels(headers) {
  const GET_SEVERITY_LEVELS_URL = `front/v1/incident-levels`;
  return axios.get(
    window.VUE_APP_MISTRA_URL + GET_SEVERITY_LEVELS_URL,
    { headers: headers }
  );
}
